import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';
import { environment } from '../../../environments/environment';

type Tab = 'announcements' | 'banners' | 'partners' | 'sponsors' | 'live-streams';

/** Backend caps the footer strip; mirrored here so the UI can warn early. */
const MAX_ACTIVE_SPONSORS = 8;

@Component({
  selector: 'app-content-admin',
  templateUrl: './content-admin.component.html',
  styleUrls: ['./content-admin.component.css'],
  providers: [MessageService],
})
export class ContentAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  tab: Tab = 'announcements';
  // Backend origin (strip trailing /api/) so stored /upload/... paths resolve.
  origin = environment.baseUrl.replace(/\/api\/?$/, '');
  /** Field name currently uploading, so only that button shows a spinner. */
  uploadingField: string | null = null;
  rows: any[] = [];
  /** Event dropdown for the live-stream form; loaded once, on first use. */
  events: any[] = [];
  loading = false;
  /** True while a save is in flight - the modal gives no other sign a slow
      network is still working, so a second click looked exactly like a stuck
      dialog until the response finally landed. */
  saving = false;
  modalOpen = false;
  editing: any = null;
  form: any = {};

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); }

  setTab(t: Tab) {
    this.tab = t; this.rows = [];
    if (t === 'live-streams') this.loadEvents();
    this.load();
  }

  /** Event dropdown, newest first - a telecast is always for a current event. */
  private loadEvents() {
    if (this.events.length) return;
    this.svc.eventList().pipe(takeUntil(this.destroy$)).subscribe(
      (r: any[]) => {
        this.events = (r || []).slice().sort((a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      },
      () => this.err('Could not load events'),
    );
  }

  load() {
    this.loading = true;
    const call =
      this.tab === 'announcements' ? this.svc.listAnnouncements() :
      this.tab === 'banners'       ? this.svc.listBanners() :
      this.tab === 'sponsors'      ? this.svc.listSponsors() :
      this.tab === 'live-streams'  ? this.svc.listLiveStreams() :
                                     this.svc.listPartners();
    call.pipe(takeUntil(this.destroy$)).subscribe((r: any[]) => {
      this.rows = r || []; this.loading = false;
    }, () => { this.loading = false; this.err('Failed to load'); });
  }

  blank(): any {
    if (this.tab === 'announcements') {
      return { message: '', linkUrl: '', audience: 'all', isActive: true, expiresAt: '',
               body: '', imageUrl: '', attachmentKey: '', attachmentName: '' };
    }
    if (this.tab === 'banners')       return { tag: 'Partner Spotlight', title: '', subtitle: '', imageUrl: '', isActive: true,
                                                h: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }] };
    if (this.tab === 'sponsors')      return { name: '', logoUrl: '', partnerType: '', isActive: true, sortOrder: this.rows.length + 1, showInStudentSidebar: false };
    if (this.tab === 'live-streams')  return { eventId: null, title: '', youtubeUrl: '', isActive: true };
    return { title: '', logoUrl: '', imageUrl: '', ctaLabel: 'Discover Offers', ctaUrl: '', isActive: true };
  }

  openNew() { if (this.tab === 'live-streams') this.loadEvents(); this.editing = null; this.form = this.blank(); this.modalOpen = true; }

  openEdit(row: any) {
    if (this.tab === 'live-streams') this.loadEvents();
    this.editing = row;
    if (this.tab === 'banners') {
      const h = (row.highlights || []).slice(0, 3);
      while (h.length < 3) h.push({ value: '', label: '' });
      this.form = { tag: row.tag, title: row.title, subtitle: row.subtitle || '', imageUrl: row.imageUrl || '', isActive: row.isActive, h };
    } else if (this.tab === 'announcements') {
      this.form = { message: row.message, linkUrl: row.linkUrl || '', audience: this.knownAudience(row.audience), isActive: row.isActive,
                    expiresAt: row.expiresAt ? row.expiresAt.substring(0, 10) : '',
                    // Without these the modal opened blank and saving wiped them.
                    body: row.body || '', imageUrl: row.imageUrl || '',
                    attachmentKey: row.attachmentKey || '', attachmentName: row.attachmentName || '' };
    } else if (this.tab === 'sponsors') {
      this.form = { name: row.name, logoUrl: row.logoUrl || '', partnerType: row.partnerType || '', isActive: row.isActive, sortOrder: row.sortOrder || 0, showInStudentSidebar: !!row.showInStudentSidebar };
    } else if (this.tab === 'live-streams') {
      this.form = { eventId: row.eventId, title: row.title || '', youtubeUrl: row.youtubeUrl || '', isActive: row.isActive };
    } else {
      this.form = { title: row.title, logoUrl: row.logoUrl || '', imageUrl: row.imageUrl || '', ctaLabel: row.ctaLabel || 'Discover Offers', ctaUrl: row.ctaUrl || '', isActive: row.isActive };
    }
    this.modalOpen = true;
  }
  close() { this.modalOpen = false; }

  private payload(): any {
    if (this.tab === 'announcements') {
      // Detail fields go with it - the modal collects body/image/attachment, and
      // leaving them out of the payload silently discarded every one of them.
      // Nulls rather than omissions, so clearing a field actually clears it.
      const b: any = {
        message:  this.form.message,
        audience: this.form.audience || 'all',
        isActive: !!this.form.isActive,
        linkUrl:  this.form.linkUrl || null,
        body:     this.form.body || null,
        imageUrl: this.form.imageUrl || null,
        attachmentKey:  this.form.attachmentKey || null,
        attachmentName: this.form.attachmentName || null,
      };
      if (this.form.expiresAt) b.expiresAt = new Date(this.form.expiresAt).toISOString();
      return b;
    }
    if (this.tab === 'banners') {
      return {
        tag: this.form.tag || 'Partner Spotlight', title: this.form.title,
        subtitle: this.form.subtitle || null, imageUrl: this.form.imageUrl || null, isActive: !!this.form.isActive,
        highlights: (this.form.h || []).filter((x: any) => x.value && x.label).map((x: any) => ({ value: x.value, label: x.label })),
      };
    }
    if (this.tab === 'live-streams') {
      return {
        eventId: Number(this.form.eventId),
        title: this.form.title || null,
        youtubeUrl: (this.form.youtubeUrl || '').trim(),
        isActive: !!this.form.isActive,
      };
    }
    if (this.tab === 'sponsors') {
      return {
        name: this.form.name, logoUrl: this.form.logoUrl, partnerType: this.form.partnerType,
        isActive: !!this.form.isActive, sortOrder: Number(this.form.sortOrder) || 0,
        showInStudentSidebar: !!this.form.isActive && !!this.form.showInStudentSidebar,
      };
    }
    return {
      title: this.form.title, logoUrl: this.form.logoUrl || null, imageUrl: this.form.imageUrl || null,
      ctaLabel: this.form.ctaLabel || 'Discover Offers', ctaUrl: this.form.ctaUrl || null, isActive: !!this.form.isActive,
    };
  }

  /** Maps a tab to its REST collection segment (used by the status toggle). */
  private collection(): string {
    return this.tab === 'announcements' ? 'announcements'
         : this.tab === 'banners'       ? 'dashboard-banners'
         : this.tab === 'sponsors'      ? 'sponsors'
         : this.tab === 'live-streams'  ? 'live-streams'
         :                                'partner-highlights';
  }

  /** Status badge in the table doubles as an on/off switch. */
  toggleActive(row: any) {
    const next = !row.isActive;
    this.svc.setContentActive(this.collection(), row.id, next)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => { row.isActive = next; this.ok(next ? 'Activated' : 'Deactivated'); },
        (e: any) => this.err((e && e.error && e.error.error) || 'Could not change status'),
      );
  }

  /** Upload a picked file and write the returned path into form[field]. */
  pickImage(ev: any, field: string) {
    const file: File = ev.target.files && ev.target.files[0];
    ev.target.value = '';                       // allow re-picking the same file
    if (!file) return;
    this.uploadingField = field;
    this.svc.uploadContentImage(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (r: any) => { this.form[field] = r.url; this.uploadingField = null; this.ok('Image uploaded'); },
        (e: any) => { this.uploadingField = null; this.err((e && e.error && e.error.error) || 'Upload failed'); },
      );
  }

  clearImage(field: string) { this.form[field] = ''; }

  /**
   * Resolve a stored image path for display. Absolute URLs pass through;
   * everything else (e.g. an uploaded "/upload/…" path) is served by the backend,
   * not by this Angular origin.
   */
  src(url: string) { return url && url.indexOf('http') === 0 ? url : this.origin + url; }

  /*
   * Images that failed to load.
   *
   * A stored path can outlive its file - seeded rows point at artwork that was
   * never uploaded, and a key under a non-public prefix is refused outright.
   * The browser then draws its broken-image glyph, which in the edit form
   * collapsed to a dot beside "Change" and read as "there is no preview here".
   * A named placeholder says which it is.
   */
  private broken: Record<string, boolean> = {};
  onImageError(url: string) { if (url) this.broken[url] = true; }
  imageBroken(url: string) { return !url || !!this.broken[url]; }

  /**
   * Name of the sponsor currently holding the student-sidebar slot, excluding
   * the row being edited. Empty when the slot is free, which is what the
   * template uses to decide whether to warn about a replacement.
   */
  sidebarSponsorName(): string {
    const editingId = this.editing ? this.editing.id : null;
    const holder = this.rows.find(r => r.showInStudentSidebar && r.isActive && r.id !== editingId);
    return holder ? holder.name : '';
  }

  /** Active sponsors excluding the one being edited - drives the cap warning. */
  activeSponsorCount(): number {
    const editingId = this.editing && this.editing.id;
    return this.rows.filter(r => r.isActive && r.id !== editingId).length;
  }

  save() {
    if (this.tab === 'sponsors') {
      if (!this.form.name)        { this.err('Name is required'); return; }
      if (!this.form.logoUrl)     { this.err('Logo URL is required'); return; }
      if (!this.form.partnerType) { this.err('Partner type is required'); return; }
      if (this.form.isActive && this.activeSponsorCount() >= MAX_ACTIVE_SPONSORS) {
        this.err('Only ' + MAX_ACTIVE_SPONSORS + ' sponsors can be active at a time. Deactivate one first.');
        return;
      }
    } else if (this.tab === 'live-streams') {
      if (!this.form.eventId)    { this.err('Pick the event this stream belongs to'); return; }
      if (!this.form.youtubeUrl) { this.err('YouTube link is required'); return; }
      // The backend parses the id too and is the authority; this only saves a
      // round trip on an obvious typo.
      if (!this.youtubeId(this.form.youtubeUrl)) {
        this.err('That does not look like a YouTube link. Paste the watch, youtu.be, live or embed URL.');
        return;
      }
    } else {
      const need = this.tab === 'announcements' ? this.form.message : this.form.title;
      if (!need) { this.err(this.tab === 'announcements' ? 'Message is required' : 'Title is required'); return; }
    }
    const b = this.payload();
    const id = this.editing && this.editing.id;
    const req =
      this.tab === 'announcements' ? (id ? this.svc.updateAnnouncement(id, b) : this.svc.saveAnnouncement(b)) :
      this.tab === 'banners'       ? (id ? this.svc.updateBanner(id, b)       : this.svc.saveBanner(b)) :
      this.tab === 'sponsors'      ? (id ? this.svc.updateSponsor(id, b)      : this.svc.saveSponsor(b)) :
      this.tab === 'live-streams'  ? (id ? this.svc.updateLiveStream(id, b)   : this.svc.saveLiveStream(b)) :
                                     (id ? this.svc.updatePartner(id, b)      : this.svc.savePartner(b));
    this.saving = true;
    req.pipe(takeUntil(this.destroy$)).subscribe(
      () => { this.saving = false; this.ok(id ? 'Saved' : 'Created'); this.close(); this.load(); },
      (e: any) => { this.saving = false; this.err((e && e.error && e.error.error) || 'Save failed'); });
  }

  remove(row: any) {
    if (!confirm('Delete this item?')) return;
    const req =
      this.tab === 'announcements' ? this.svc.deleteAnnouncement(row.id) :
      this.tab === 'banners'       ? this.svc.deleteBanner(row.id) :
      this.tab === 'sponsors'      ? this.svc.deleteSponsor(row.id) :
      this.tab === 'live-streams'  ? this.svc.deleteLiveStream(row.id) :
                                     this.svc.deletePartner(row.id);
    req.pipe(takeUntil(this.destroy$)).subscribe(() => { this.ok('Deleted'); this.load(); }, () => this.err('Delete failed'));
  }

  /**
   * The 11-char video id out of any YouTube link shape. Mirrors the backend
   * parser so the form can reject a bad paste and preview a good one.
   */
  youtubeId(input: string): string {
    const raw = (input || '').trim();
    if (/^[\w-]{11}$/.test(raw)) return raw;
    const m = raw.match(/(?:youtu\.be\/|\/live\/|\/embed\/|\/shorts\/|\/v\/|[?&]v=)([\w-]{11})/);
    return m ? m[1] : '';
  }

  /** Thumbnail preview so the admin can see they pasted the right stream. */
  thumb(url: string): string {
    const id = this.youtubeId(url);
    return id ? 'https://i.ytimg.com/vi/' + id + '/mqdefault.jpg' : '';
  }

  eventLabel(e: any): string {
    const d = e && e.startDate ? new Date(e.startDate) : null;
    const when = d ? ' (' + d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ')' : '';
    return (e && (e.event_name || e.name) ? (e.event_name || e.name) : 'Event') + when;
  }

  /**
   * Whether the portals are showing this feed right now. Active alone is not
   * the answer - a feed only appears while its event is running, so the table
   * has to say "On air" versus "Scheduled" or the admin cannot tell why a link
   * they switched on is nowhere to be seen.
   */
  streamState(row: any): 'on-air' | 'scheduled' | 'ended' | 'off' {
    if (!row.isActive) return 'off';
    const ev = row.event;
    if (!ev) return 'scheduled';
    const now = new Date();
    const start = new Date(ev.startDate);
    const end = new Date(ev.endDate || ev.startDate);
    end.setHours(23, 59, 59, 999);
    if (now > end) return 'ended';
    return now >= start ? 'on-air' : 'scheduled';
  }

  private ok(m: string)  { this.toast.add({ severity: 'success', summary: m }); }
  private err(m: string) { this.toast.add({ severity: 'error', summary: m }); }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
  /**
   * Who sees an announcement. Zone values target the schools in that zone (and
   * the students belonging to them); 'schools'/'students' target one portal
   * across every zone.
   */
  /**
   * Rows written before the audience list was fixed hold 'school'/'student'
   * singular. The select has no such option, so it rendered blank and saving
   * would have sent an empty audience.
   */
  knownAudience(a: string): string {
    if (a === 'school')  return 'schools';
    if (a === 'student') return 'students';
    return this.audienceOptions.some(o => o.value === a) ? a : 'all';
  }

  audienceOptions = [
    { value: 'all',      label: 'Everyone (schools & students)' },
    { value: 'schools',  label: 'All schools' },
    { value: 'students', label: 'All students' },
    { value: 'NORTH',    label: 'North zone' },
    { value: 'SOUTH',    label: 'South zone' },
    { value: 'EAST',     label: 'East zone' },
    { value: 'WEST',     label: 'West zone' },
  ];
  uploadingImage = false;
  uploadingFile = false;

  /** Image shown at the top of the school's update modal. */
  uploadAnnouncementImage(ev: any) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (!f) { return; }
    this.uploadingImage = true;
    this.svc.uploadContentImage(f).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.form.imageUrl = r.url; this.uploadingImage = false; },
      () => { this.uploadingImage = false; this.err('Image upload failed'); },
    );
  }

  /** Downloadable circular or form. */
  uploadAnnouncementFile(ev: any) {
    const f = ev && ev.target && ev.target.files && ev.target.files[0];
    if (!f) { return; }
    this.uploadingFile = true;
    const fd = new FormData();
    fd.append('file', f, f.name);
    this.svc.uploadAnnouncementFile(fd).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.form.attachmentKey = r.attachmentKey;
        this.form.attachmentName = r.attachmentName;
        this.uploadingFile = false;
      },
      () => { this.uploadingFile = false; this.err('Attachment upload failed'); },
    );
  }
}
